"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";

type BlogItem = {
	blog_id: number;
	blog_title: string;
	blog_slug: string;
	blog_cover_origin: string;
	blog_cover_type: boolean;
	blog_excerpt: string;
	blog_upload_time: string;
};

const FAQ_KEYS = ["faq1", "faq2", "faq3", "faq4", "faq5"] as const;

function FAQItem({ qKey, isOpen, onToggle }: { qKey: string; isOpen: boolean; onToggle: () => void }) {
	const t = useTranslations("homeBeauty");

	return (
		<div className="border-brand-ink/8 border-b">
			<button
				type="button"
				onClick={onToggle}
				className="flex w-full items-center justify-between gap-4 py-5 text-left"
				aria-expanded={isOpen}
			>
				<span className="font-display text-[18px] leading-[1.3] text-brand-ink md:text-[20px] lg:text-[21px]">
					{t(`${qKey}_q`) || qKey}
				</span>
				<span
					className="flex h-7 w-7 shrink-0 items-center justify-center text-brand-muted transition-transform duration-300"
					style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0)" }}
					aria-hidden
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 16 16"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
					>
						<path d="M8 2v12M2 8h12" />
					</svg>
				</span>
			</button>
			<div
				className="duration-400 grid transition-all ease-out"
				style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
			>
				<div className="overflow-hidden">
					<p className="pb-5 font-body text-[15px] leading-[1.72] text-brand-muted lg:text-[16px]">
						{t(`${qKey}_a`) || ""}
					</p>
				</div>
			</div>
		</div>
	);
}

export default function BlogAndFAQ({ blogList }: { blogList: BlogItem[] }) {
	const t = useTranslations("homeBeauty");
	const [openFaq, setOpenFaq] = useState<string | null>(FAQ_KEYS[0]);

	const items = blogList.slice(0, 6);
	const [feature, ...rest] = items;

	return (
		<>
			{/* Blog */}
			{items.length > 0 && (
				<section className="w-full bg-white py-16 sm:py-20 md:py-24">
					<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
						<header className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
							<div>
								<span className="inline-flex items-center gap-2 font-body text-[12px] font-medium uppercase tracking-[0.2em] text-brand-fg xl:text-[13px]">
									<span aria-hidden className="inline-block h-px w-8 bg-brand" />
									{t("blogEyebrow")}
								</span>
								<h2 className="mt-4 font-display text-[clamp(32px,3.6vw,60px)] leading-[1.1] text-brand-ink">
									{t("blogTitle")}
								</h2>
							</div>
							<Link
								href="/blog"
								className="border-brand-ink/42 group inline-flex items-center gap-2 border-b pb-1 font-body text-[13px] font-semibold uppercase tracking-[0.22em] text-brand-ink transition-colors duration-200 hover:border-brand hover:!text-brand-fg xl:text-[14px]"
							>
								{t("blogViewAll")}
								<span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">
									→
								</span>
							</Link>
						</header>

						{feature && (
							<div className="mt-12 hidden grid-cols-12 gap-8 md:grid">
								<Link
									href={`/blog/${feature.blog_slug}`}
									className="group col-span-7 block text-brand-ink hover:!text-brand-ink"
								>
									<div className="relative aspect-[16/11] w-full overflow-hidden bg-brand-softer">
										{feature.blog_cover_type ? (
											<Image
												src={feature.blog_cover_origin}
												alt={feature.blog_title}
												fill
												priority
												sizes="(max-width:1024px) 60vw, 720px"
												className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
											/>
										) : (
											<video
												autoPlay
												muted
												loop
												playsInline
												className="absolute inset-0 h-full w-full object-cover"
												src={feature.blog_cover_origin}
											/>
										)}
									</div>
									<h3 className="mt-5 font-display text-[30px] leading-[1.2] text-brand-ink md:text-[34px] lg:text-[36px]">
										{feature.blog_title}
									</h3>
									<p
										className="mt-2 line-clamp-2 max-w-[56ch] font-body text-[16px] leading-[1.72] text-brand-muted"
										dangerouslySetInnerHTML={{ __html: feature.blog_excerpt }}
									/>
								</Link>

								<div className="divide-brand-ink/8 col-span-5 flex flex-col divide-y">
									{rest.map((item) => (
										<Link
											key={item.blog_id}
											href={`/blog/${item.blog_slug}`}
											className="group flex gap-5 py-5 text-brand-ink first:pt-0 last:pb-0 hover:!text-brand-ink"
										>
											<div className="relative aspect-square w-[110px] shrink-0 overflow-hidden bg-brand-softer">
												{item.blog_cover_type ? (
													<Image
														src={item.blog_cover_origin}
														alt={item.blog_title}
														fill
														sizes="110px"
														className="object-cover transition-transform duration-700 group-hover:scale-[1.06]"
													/>
												) : (
													<video
														autoPlay
														muted
														loop
														playsInline
														className="absolute inset-0 h-full w-full object-cover"
														src={item.blog_cover_origin}
													/>
												)}
											</div>
											<div className="flex flex-1 flex-col">
												<h4 className="line-clamp-2 font-display text-[19px] leading-[1.25] text-brand-ink transition-colors duration-200 group-hover:text-brand-fg lg:text-[20px]">
													{item.blog_title}
												</h4>
												<span className="mt-auto pt-3 font-body text-[12px] font-semibold uppercase tracking-[0.22em] text-brand-fg xl:text-[13px]">
													{t("blogReadMore")} →
												</span>
											</div>
										</Link>
									))}
								</div>
							</div>
						)}

						{/* mobile */}
						<div className="mt-10 flex flex-col gap-6 md:hidden">
							{items.map((item) => (
								<Link
									key={item.blog_id}
									href={`/blog/${item.blog_slug}`}
									className="group block text-brand-ink hover:!text-brand-ink"
								>
									<div className="relative aspect-[4/3] w-full overflow-hidden bg-brand-softer">
										{item.blog_cover_type ? (
											<Image
												src={item.blog_cover_origin}
												alt={item.blog_title}
												fill
												sizes="100vw"
												className="object-cover"
											/>
										) : (
											<video
												autoPlay
												muted
												loop
												playsInline
												className="absolute inset-0 h-full w-full object-cover"
												src={item.blog_cover_origin}
											/>
										)}
									</div>
									<h3 className="mt-3 line-clamp-2 font-display text-[22px] leading-[1.2] text-brand-ink sm:text-[23px]">
										{item.blog_title}
									</h3>
								</Link>
							))}
						</div>
					</div>
				</section>
			)}

			{/* FAQ */}
			<section className="w-full bg-brand-tint py-16 sm:py-20 md:py-24">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
						<div className="md:col-span-4">
							<span className="inline-flex items-center gap-2 font-body text-[12px] font-medium uppercase tracking-[0.2em] text-brand-fg xl:text-[13px]">
								<span aria-hidden className="inline-block h-px w-8 bg-brand" />
								FAQ
							</span>
							<h2 className="mt-4 font-display text-[clamp(30px,3.25vw,50px)] leading-[1.12] text-brand-ink">
								{t("faqTitle") || "Frequently Asked Questions"}
							</h2>
							<p className="mt-3 font-body text-[16px] leading-[1.72] text-brand-muted">
								{t("faqDesc") || "Find answers to common questions about our products and services."}
							</p>
						</div>
						<div className="md:col-span-8">
							{FAQ_KEYS.map((key) => (
								<FAQItem
									key={key}
									qKey={key}
									isOpen={openFaq === key}
									onToggle={() => setOpenFaq(openFaq === key ? null : key)}
								/>
							))}
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
